import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import {
  getPosBillByNumber,
  posError,
  readTillCounter,
  savePosBill,
} from "../../../../services/PosApiServices";
import { InvSales } from "../../../../core/json/invSales";
import { FinancialYear } from "../../../../core/json/FinancialYear";
import { billTotals, lineTotals, num } from "../posHelpers";
import { printPosBill, useReceiptHeader } from "./posPrint";
import { amt, day, time, Empty, PageHead } from "./PosParts";

const REFUND_MODES = [
  { value: 0, key: "cash", label: "Cash" },
  { value: 3, key: "card", label: "Card" },
  { value: 2, key: "upi", label: "UPI" },
  { value: 1, key: "credit", label: "Credit note" },
];

const round2 = (v) => Math.round(num(v) * 100) / 100;
const same = (a, b) => `${a ?? ""}`.toLowerCase() === `${b ?? ""}`.toLowerCase();

/**
 * POS ▸ Sales Return, against a POS bill. Each line goes back at the price,
 * discount and tax it was sold at, the bill's own discount and extra charge
 * are shared out in proportion, and no line can return more than the bill
 * sold less earlier returns (the API checks this too). The return is a sales
 * return (SR, "POSR…") in this counter's open shift.
 */
const PosReturn = ({ branch, userId, state, permit }) => {
  const header = useReceiptHeader();
  const { branchsettings } = useSelector((s) => s.settings);
  const { companyInfolist } = useSelector((s) => s.companyInfo);

  const counters = useMemo(() => state?.counters || [], [state]);
  const [counterID, setCounterID] = useState("");
  const [billNo, setBillNo] = useState("");
  const [bill, setBill] = useState(null);
  const [qty, setQty] = useState({});
  const [mode, setMode] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (counterID && counters.some((c) => same(c.counterID, counterID))) return;
    const own = counters.find((c) => same(c.counterID, readTillCounter(branch?.guid)));
    setCounterID((own || counters[0])?.counterID || "");
  }, [counters, counterID, branch]);

  // returnedQty is per product; when a product sits on several lines, earlier
  // returns are taken off those lines in order
  const lines = useMemo(() => {
    const taken = {};
    return (bill?.lines || []).map((l) => {
      const key = `${l.productID}`.toLowerCase();
      if (taken[key] === undefined) taken[key] = num(l.returnedQty);
      const fromThis = Math.min(num(l.quantity), taken[key]);
      taken[key] -= fromThis;
      return { ...l, returnedHere: fromThis, returnable: Math.max(0, round2(num(l.quantity) - fromThis)) };
    });
  }, [bill]);

  const returnLines = useMemo(
    () =>
      lines
        .map((l) => {
          const q = Math.min(num(qty[l.guid]), l.returnable);
          const t = lineTotals({ quantity: q, price: l.price, discountPercent: l.discPercentage, taxPercent: l.taxPercentage });
          return { line: l, quantity: q, ...t };
        })
        .filter((r) => r.quantity > 0),
    [lines, qty]
  );

  // the returned items take their share of the bill's discount / extra charge
  // (by taxable amount), and VAT is worked out on what is left, as on the bill
  const totals = useMemo(() => {
    const billTaxable = lines.reduce(
      (t, l) =>
        t + lineTotals({ quantity: l.quantity, price: l.price, discountPercent: l.discPercentage, taxPercent: 0 }).taxable,
      0
    );
    const returnTaxable = returnLines.reduce((t, r) => t + r.taxable, 0);
    const share = billTaxable > 0 ? returnTaxable / billTaxable : 0;
    const billDiscount = round2(num(bill?.billDiscount) * share);
    const extra = round2(num(bill?.additionalAmt) * share);
    const calc = billTotals(
      returnLines.map((r) => ({
        quantity: r.quantity,
        price: r.line.price,
        discountPercent: r.line.discPercentage,
        taxPercent: r.line.taxPercentage,
      })),
      { billDiscount, extraCharge: extra }
    );
    return {
      gross: calc.gross,
      lineDiscount: calc.lineDiscount,
      billDiscount,
      extra,
      tax: calc.tax,
      net: calc.net,
      lines: calc.lines,
    };
  }, [lines, returnLines, bill]);

  const loadBill = async () => {
    const no = billNo.trim();
    if (!no || !branch?.guid) return;
    try {
      const found = await getPosBillByNumber(branch.guid, no);
      if (found.transType !== 2) {
        toast.error(`${found.entryNumber} is a return, not a bill`);
        return;
      }
      setBill(found);
      setQty({});
      // a credit bill is refunded as a credit note, anything else in cash
      const creditOnly = num(found.credit) > 0 && !num(found.cash) && !num(found.card) && !num(found.upi);
      setMode(creditOnly ? 1 : 0);
    } catch (error) {
      toast.error(posError(error, `There is no POS bill ${no}`));
      setBill(null);
    }
  };

  const reset = () => {
    setBill(null);
    setQty({});
    setRemarks("");
    setBillNo("");
  };

  const save = async () => {
    if (!bill || !returnLines.length) {
      toast.error("Enter the quantity to return on at least one line");
      return;
    }
    if (!counterID) {
      toast.error("Pick the counter taking the return");
      return;
    }
    const refund = REFUND_MODES.find((m) => m.value === mode);
    const answer = await Swal.fire({
      title: `Return ${amt(totals.net)} on bill ${bill.entryNumber}?`,
      text: `Refund by ${refund.label}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4B44D6",
      cancelButtonColor: "#67728A",
      confirmButtonText: "Save return",
    });
    if (!answer.isConfirmed) return;

    const now = dayjs();
    const stamp = now.format("YYYY-MM-DDTHH:mm:ss");
    const ret = new InvSales();
    const fy = new FinancialYear();
    fy.id = branchsettings?.defaultFinancialYearID;

    ret.counterID = counterID;
    ret.branch = branch;
    ret.invdate = stamp; // the server keeps the time and puts it on the business date
    ret.deliveryDate = stamp;
    ret.salesTransType = 3;
    ret.salesPaymentType = mode;
    ret.salesMode = 0;
    ret.reftype = 3;
    ret.refNo = bill.entryNumber;
    ret.currency = "00000000-0000-0000-0000-000000000000";
    ret.customer = { guid: bill.customerID, name: bill.customerName };
    ret.customerName = bill.customerName || "";
    ret.customerContactno = bill.contactNumber || "";
    ret.location = bill.locationID ? { guid: bill.locationID } : null;
    ret.terms = (remarks || "").slice(0, 50);
    ret.dispatch = "";
    ret.dispatchTrough = "";
    ret.createdUser = `${userId?.id ?? ""}`;
    ret.updatedUser = `${userId?.id ?? ""}`;
    ret.financialYear = fy;
    ret.Company = companyInfolist?.[0];

    ret.grossTotal = totals.gross;
    ret.discAmt = totals.billDiscount;
    ret.discountTotal = round2(totals.lineDiscount + totals.billDiscount);
    ret.additionalAmt = totals.extra;
    ret.taxTotal = totals.tax;
    ret.roundOff = 0;
    ret.netTotal = totals.net;
    ret.paid = totals.net;
    ret.change = 0;
    ret.cash = refund.key === "cash" ? totals.net : 0;
    ret.card = refund.key === "card" ? totals.net : 0;
    ret.upi = refund.key === "upi" ? totals.net : 0;
    ret.bank = refund.key === "credit" ? totals.net : 0;

    ret.invSalesDetails = returnLines.map((r, i) => ({
      product: { guid: r.line.productID, name: r.line.productName },
      unit: { guid: r.line.unitID },
      itemCode: r.line.productCode || "",
      description: r.line.productName || "",
      batch: r.line.batch || "",
      expiryDate: stamp,
      quantity: r.quantity,
      price: num(r.line.price),
      mrp: num(r.line.mrp),
      foc: 0,
      grossAmount: totals.lines[i].gross,
      discPercentage: num(r.line.discPercentage),
      discAmt: totals.lines[i].discountAmount,
      taxPercentage: num(r.line.taxPercentage),
      taxamnt: totals.lines[i].taxAmount,
      netAmt: totals.lines[i].net,
      notes: "",
    }));

    setBusy(true);
    try {
      const saved = await savePosBill(ret);
      toast.success(`Return ${saved.entryNumber} saved`);
      try {
        printPosBill(await getPosBillByNumber(branch.guid, saved.entryNumber), header);
      } catch {
        toast.error("The return was saved; print it from Invoice Re-Print");
      }
      reset();
    } catch (error) {
      toast.error(posError(error, "Could not save the return"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        title="POS Sales Return"
        subtitle={`Against a POS bill · business date ${day(state?.transactionDate)}`}
      >
        <select className="form-select" value={counterID} onChange={(e) => setCounterID(e.target.value)} aria-label="Counter">
          {counters.map((c) => (
            <option key={c.counterID} value={c.counterID}>
              {c.counterName} · shift {c.shiftNo}
            </option>
          ))}
        </select>
      </PageHead>

      <div className="pm-card">
        <div className="pm-search">
          <input
            className="form-control"
            placeholder="Bill number, e.g. POS125"
            value={billNo}
            onChange={(e) => setBillNo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadBill()}
            autoFocus
          />
          <button type="button" className="btn btn-pm" onClick={loadBill}>
            Load bill
          </button>
          {bill ? (
            <button type="button" className="btn btn-light" onClick={reset}>
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {!bill ? (
        <div className="pm-card">
          <Empty>Load the POS bill the customer is returning goods from.</Empty>
        </div>
      ) : (
        <div className="pm-cols">
          <div className="pm-card">
            <h5>
              Bill {bill.entryNumber}{" "}
              <span className="pm-pill">
                {day(bill.transactionDate)} {time(bill.invDate)}
              </span>
            </h5>
            <p className="text-muted small">
              {bill.customerName || "Walk-in"} · {bill.counterName} · net {amt(bill.netTotal)}
            </p>
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Rate</th>
                    <th className="num">Sold</th>
                    <th className="num">Returned</th>
                    <th className="num">Return now</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.guid}>
                      <td>
                        <div>
                          <b>{l.productName}</b>
                        </div>
                        <div className="text-muted small">
                          {[l.productCode, l.unitName, num(l.discPercentage) ? `${num(l.discPercentage)}% off` : ""]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </td>
                      <td className="num">{amt(l.price)}</td>
                      <td className="num">{num(l.quantity)}</td>
                      <td className="num">{l.returnedHere || "—"}</td>
                      <td className="num">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          max={l.returnable}
                          step="any"
                          className="form-control pm-qty d-inline-block"
                          value={qty[l.guid] ?? ""}
                          disabled={!l.returnable}
                          placeholder={l.returnable ? "0" : "—"}
                          onChange={(e) => {
                            const v = e.target.value;
                            setQty((p) => ({ ...p, [l.guid]: v === "" ? "" : `${Math.min(Math.max(0, num(v)), l.returnable)}` }));
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-light mt-2"
              onClick={() => setQty(Object.fromEntries(lines.map((l) => [l.guid, l.returnable ? `${l.returnable}` : ""])))}
            >
              Return everything left
            </button>
          </div>

          <div className="pm-card">
            <h5>Refund</h5>
            <table className="pm-sum mb-3">
              <tbody>
                <tr>
                  <td>Items</td>
                  <td>{amt(totals.gross)}</td>
                </tr>
                {totals.lineDiscount ? (
                  <tr>
                    <td>Line discounts</td>
                    <td>-{amt(totals.lineDiscount)}</td>
                  </tr>
                ) : null}
                {totals.billDiscount ? (
                  <tr>
                    <td>Share of bill discount</td>
                    <td>-{amt(totals.billDiscount)}</td>
                  </tr>
                ) : null}
                {totals.extra ? (
                  <tr>
                    <td>Share of extra charge</td>
                    <td>{amt(totals.extra)}</td>
                  </tr>
                ) : null}
                {totals.tax ? (
                  <tr>
                    <td>Tax</td>
                    <td>{amt(totals.tax)}</td>
                  </tr>
                ) : null}
                <tr className="pm-sum__total">
                  <td>Refund</td>
                  <td>{amt(totals.net)}</td>
                </tr>
              </tbody>
            </table>
            <div className="pm-field">
              <label>Refund by</label>
              <div className="pm-modes">
                {REFUND_MODES.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    className={`btn ${mode === m.value ? "btn-pm" : "btn-light"}`}
                    onClick={() => setMode(m.value)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="pm-field">
              <label htmlFor="pm-ret-remarks">Remarks</label>
              <input
                id="pm-ret-remarks"
                className="form-control"
                maxLength={50}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-pm w-100"
              onClick={save}
              disabled={busy || !returnLines.length || !permit.add}
              title={permit.add ? "" : "You do not have the right to save a return"}
            >
              {busy ? "Saving…" : "Save & print return"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

PosReturn.propTypes = {
  branch: PropTypes.object,
  userId: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  state: PropTypes.object,
  permit: PropTypes.object.isRequired,
};

export default PosReturn;
